import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/hooks/useAnalytics';
import { identifyPostHogUser, resetPostHogUser } from '@/hooks/usePostHog';

interface Profile {
  id: string;
  full_name: string | null;
  purpose: string | null;
  avatar_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { full_name?: string; purpose?: string }) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  updateEmail: (newEmail: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const hasInitialized = useRef(false);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (!error && data) {
      setProfile(data);
    }
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Don't flip loading to false until initialization is complete.
        // Otherwise route guards can redirect to /login before we process OAuth tokens/codes.
        if (session?.user || hasInitialized.current) {
          setLoading(false);
        }

        // Fetch profile after auth state change (deferred to avoid deadlock)
        if (session?.user) {
          setTimeout(async () => {
            await fetchProfile(session.user.id);
            
            // Identify user in PostHog
            const { data: profileData } = await supabase
              .from('profiles')
              .select('full_name, partner_subdomain_id')
              .eq('id', session.user.id)
              .single();
            
            // Check if user has admin role
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', session.user.id)
              .single();
            
            const isAdmin = roleData?.role === 'admin';
            const isPartner = Boolean(profileData?.partner_subdomain_id);
            
            identifyPostHogUser(session.user.id, {
              email: session.user.email,
              name: profileData?.full_name || undefined,
              userType: isAdmin ? 'admin' : isPartner ? 'partner' : 'user',
              partnerId: profileData?.partner_subdomain_id || undefined,
            });

            // Handle pending invitation code from registration
            const pendingCode = localStorage.getItem('pending_invite_code');
            if (pendingCode && event === 'SIGNED_IN') {
              supabase.functions.invoke('manage-invitations', {
                body: { action: 'mark_used', code: pendingCode, user_id: session.user.id }
              }).then(() => {
                localStorage.removeItem('pending_invite_code');
              }).catch(console.error);
            }

            // Handle pending referral code from registration
            const pendingReferral = localStorage.getItem('pending_referral_code');
            if (pendingReferral && event === 'SIGNED_IN') {
              supabase.functions.invoke('manage-referrals', {
                body: { action: 'complete', referral_code: pendingReferral, user_id: session.user.id }
              }).then(() => {
                localStorage.removeItem('pending_referral_code');
              }).catch(console.error);
            }
          }, 0);
        } else {
          setProfile(null);
          resetPostHogUser();
        }
      }
    );

    const initialize = async () => {
      try {
        // Some OAuth setups return an implicit flow hash (/#access_token=...)
        // If it exists, manually store the session so subsequent requests are authenticated.
        if (window.location.hash) {
          const hash = window.location.hash.startsWith('#')
            ? window.location.hash.slice(1)
            : window.location.hash;
          const hashParams = new URLSearchParams(hash);
          const access_token = hashParams.get('access_token');
          const refresh_token = hashParams.get('refresh_token');

          if (access_token && refresh_token) {
            await supabase.auth.setSession({ access_token, refresh_token });

            // Clean up URL (remove tokens from address bar)
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + window.location.search
            );
          }
        }

        // Handle PKCE code flow if present
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        if (code) {
          await supabase.auth.exchangeCodeForSession(code);
          url.searchParams.delete('code');
          url.searchParams.delete('error');
          url.searchParams.delete('error_code');
          url.searchParams.delete('error_description');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? url.search : ''));
        }
      } catch (e) {
        // Avoid crashing app during auth init; the UI will fall back to logged-out state
        console.warn('Auth initialization warning:', e);
      }

      // THEN check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      hasInitialized.current = true;
      setLoading(false);

      if (session?.user) {
        fetchProfile(session.user.id);
      }
    };

    void initialize();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: { full_name?: string; purpose?: string }) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });
    
    if (!error) {
      trackEvent('sign_up', { category: 'auth', label: 'email' });
    }
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (!error) {
      trackEvent('login', { category: 'auth', label: 'email' });
    }
    
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    
    if (!error) {
      trackEvent('login', { category: 'auth', label: 'google' });
    }
    
    return { error: error as Error | null };
  };

  const signInWithApple = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    
    if (!error) {
      trackEvent('login', { category: 'auth', label: 'apple' });
    }
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Clear local state immediately to prevent stale redirects
    setUser(null);
    setSession(null);
    setProfile(null);
    
    trackEvent('logout', { category: 'auth' });
    
    // Reset PostHog user
    resetPostHogUser();
    
    // Attempt server signout (may fail if session already expired)
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Ignore errors - local state is already cleared
      console.log('SignOut error (session may already be expired):', error);
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    
    if (!error) {
      trackEvent('password_reset', { category: 'auth' });
    }
    
    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { error: error as Error | null };
  };

  const updateEmail = async (newEmail: string) => {
    const { error } = await supabase.auth.updateUser({
      email: newEmail
    });
    
    if (!error) {
      trackEvent('email_change_requested', { category: 'auth' });
    }
    
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signUp,
      signIn,
      signInWithGoogle,
      signInWithApple,
      signOut,
      resetPassword,
      updatePassword,
      updateEmail,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
